import { useCallback, useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cah_admin_token";

const QUICK_PROMPTS = {
  marketing: [
    "Draft a 4-week launch campaign plan for this event",
    "Which channels deserve the most budget, and why?",
    "Suggest a Zepto + WhatsApp activation idea",
  ],
  creative: [
    "Write 10 Google Ads headlines (max 30 characters)",
    "Write 3 Instagram captions in the brand voice",
    "Give me 5 short-form video concepts",
  ],
  sales: [
    "A visitor asks: can I bring my 70-year-old parents?",
    "What friction might stop people from booking?",
    "Write a WhatsApp reply for 'how much are tickets?'",
  ],
  analytics: [
    "What does the current traffic data tell us?",
    "Which CTA location drives the most BookMyShow clicks?",
    "Where is the funnel leaking?",
  ],
  research: [
    "Research the Bengaluru audience for sufi and qawwali events",
    "Suggest Google Ads keywords with intent notes",
    "What creative trends work for audiences aged 35-80?",
  ],
};

function MetricCard({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-[#D4AF37]/20 bg-[#161310] px-5 py-4"
    >
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{label}</p>
      <p className="font-display mt-1 text-2xl text-[#F7F4EF]">{value}</p>
    </div>
  );
}

export default function Console() {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(TOKEN_KEY) || "";
    } catch {
      return "";
    }
  });
  const [tokenInput, setTokenInput] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [authError, setAuthError] = useState("");
  const [agents, setAgents] = useState([]);
  const [active, setActive] = useState("marketing");
  const [sessions, setSessions] = useState({});
  const [threads, setThreads] = useState({});
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [showLeads, setShowLeads] = useState(false);
  const [leads, setLeads] = useState(null);
  const bottomRef = useRef(null);

  const headers = useCallback(
    () => ({ "Content-Type": "application/json", "X-Admin-Token": token }),
    [token]
  );

  const loadMetrics = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/analytics/summary`, { headers: headers() });
      if (res.ok) setMetrics(await res.json());
    } catch {
      /* metrics unavailable */
    }
  }, [headers]);

  const loadLeads = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/sponsor-leads`, { headers: headers() });
      if (res.ok) setLeads((await res.json()).leads);
    } catch {
      /* unavailable */
    }
  }, [headers]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/agents`, { headers: headers() });
        if (res.ok) {
          const data = await res.json();
          setAgents(data.agents);
          setUnlocked(true);
          loadMetrics();
        }
      } catch {
        /* stay locked */
      }
    })();
  }, [token, headers, loadMetrics]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [threads, active]);

  const unlock = async () => {
    setAuthError("");
    try {
      const res = await fetch(`${API}/agents`, {
        headers: { "Content-Type": "application/json", "X-Admin-Token": tokenInput.trim() },
      });
      if (!res.ok) throw new Error("Invalid token");
      const data = await res.json();
      try {
        localStorage.setItem(TOKEN_KEY, tokenInput.trim());
      } catch {
        /* ignore */
      }
      setToken(tokenInput.trim());
      setAgents(data.agents);
      setUnlocked(true);
    } catch {
      setAuthError("That token did not work. Check the admin token and try again.");
    }
  };

  const signOut = () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
    setToken("");
    setUnlocked(false);
    setAgents([]);
    setThreads({});
  };

  const appendToLast = useCallback((key, text) => {
    setThreads((t) => {
      const arr = [...(t[key] || [])];
      const last = arr[arr.length - 1];
      arr[arr.length - 1] = { ...last, content: last.content + text };
      return { ...t, [key]: arr };
    });
  }, []);

  const send = async (preset) => {
    const message = (preset ?? input).trim();
    if (!message || busy) return;
    const key = active;
    setInput("");
    setBusy(true);
    setThreads((t) => ({
      ...t,
      [key]: [...(t[key] || []), { role: "user", content: message }, { role: "assistant", content: "" }],
    }));
    try {
      const res = await fetch(`${API}/agents/${key}/chat`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ message, session_id: sessions[key] }),
      });
      if (!res.ok || !res.body) throw new Error(`Request failed (${res.status})`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let data;
          try {
            data = JSON.parse(line.slice(5).trim());
          } catch {
            continue;
          }
          if (data.token) appendToLast(key, data.token);
          if (data.error) appendToLast(key, `\n\n[Error: ${data.error}]`);
          if (data.session_id) setSessions((s) => ({ ...s, [key]: data.session_id }));
        }
      }
    } catch (e) {
      appendToLast(key, `\n\n[${e.message || "Something went wrong. Please try again."}]`);
    }
    setBusy(false);
  };

  const activeAgent = agents.find((a) => a.key === active);
  const thread = threads[active] || [];
  const topSource =
    metrics && metrics.events_by_source && Object.keys(metrics.events_by_source).length
      ? Object.entries(metrics.events_by_source).sort((a, b) => b[1] - a[1])[0][0]
      : "—";

  if (!unlocked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#080705] px-5 text-[#F7F4EF]">
        <div className="w-full max-w-md rounded-3xl border border-[#D4AF37]/25 bg-[#161310] p-8 sm:p-10">
          <img src="/brand/logo-small.webp" alt="Chitti Ayee Hai — official logo" className="h-16 w-auto" />
          <h1 className="font-display mt-4 text-2xl sm:text-3xl">Command Console</h1>
          <p className="mt-2 text-sm leading-relaxed text-[#918A7E] sm:text-base">
            Chitti Ayee Hai — AI marketing &amp; sales platform. Enter the admin token to continue.
          </p>
          <input
            type="password"
            data-testid="console-token-input"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && unlock()}
            placeholder="Admin token"
            className="mt-6 min-h-[52px] w-full rounded-xl border border-[#D4AF37]/30 bg-[#0E0C0A] px-4 text-base text-[#F7F4EF] placeholder:text-[#918A7E] focus:border-[#D4AF37] focus:outline-none"
          />
          {authError && (
            <p data-testid="console-auth-error" className="mt-3 text-sm text-red-300">
              {authError}
            </p>
          )}
          <button
            type="button"
            data-testid="console-unlock-button"
            onClick={unlock}
            className="btn-gold mt-5 min-h-[52px] w-full rounded-full text-sm font-bold uppercase tracking-[0.12em] text-[#0A0805]"
          >
            Open Console
          </button>
          <a
            href="/"
            data-testid="console-view-site-link"
            className="mt-5 block text-center text-sm text-[#918A7E] transition-colors hover:text-[#E6CA65]"
          >
            Back to the website
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080705] text-[#F7F4EF]">
      <header className="sticky top-0 z-40 border-b border-[#D4AF37]/15 bg-[#080705]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <img src="/brand/logo-small.webp" alt="Chitti Ayee Hai — official logo" className="h-11 w-auto" />
            <div>
              <p className="font-display text-sm uppercase tracking-[0.2em] sm:text-base">
                Command Console
              </p>
              <p className="text-xs text-[#918A7E]">Chitti Ayee Hai · AI marketing &amp; sales platform</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              data-testid="console-view-site-link"
              className="hidden text-sm text-[#C8C2B7] transition-colors hover:text-[#E6CA65] sm:block"
            >
              View site
            </a>
            <button
              type="button"
              data-testid="console-signout-button"
              onClick={signOut}
              className="rounded-full border border-[#D4AF37]/30 px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-5 py-8 sm:px-8 lg:grid-cols-[300px_1fr]">
        <aside className="space-y-4">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-1">
            <MetricCard label="Tracked Events" value={metrics ? metrics.total_events : "…"} testId="metric-total-events" />
            <MetricCard label="Unique Visitors" value={metrics ? metrics.unique_visitors : "…"} testId="metric-unique-visitors" />
            <MetricCard label="BookMyShow Clicks" value={metrics ? metrics.bookmyshow_clicks : "…"} testId="metric-bookmyshow-clicks" />
            <MetricCard label="Top Source" value={metrics ? topSource : "…"} testId="metric-top-source" />
          </div>
          <nav className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible" aria-label="Agents">
            {agents.map((a) => (
              <button
                key={a.key}
                type="button"
                data-testid={`agent-tab-${a.key}`}
                onClick={() => {
                  setActive(a.key);
                  setShowLeads(false);
                }}
                className={`min-w-[160px] rounded-2xl border px-4 py-3 text-left transition-colors lg:min-w-0 ${
                  active === a.key
                    ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.1]"
                    : "border-[#D4AF37]/15 bg-[#161310] hover:border-[#D4AF37]/40"
                }`}
              >
                <p className="font-display text-base text-[#F7F4EF]">{a.name}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[#918A7E]">
                  {a.description}
                </p>
              </button>
            ))}
          </nav>
          <button
            type="button"
            data-testid="leads-tab"
            onClick={() => {
              setShowLeads(true);
              loadLeads();
            }}
            className={`w-full rounded-2xl border px-4 py-3 text-left transition-colors ${
              showLeads
                ? "border-[#D4AF37]/60 bg-[#D4AF37]/[0.1]"
                : "border-[#D4AF37]/15 bg-[#161310] hover:border-[#D4AF37]/40"
            }`}
          >
            <p className="font-display text-base text-[#F7F4EF]">Sponsor Leads</p>
            <p className="mt-1 text-xs leading-relaxed text-[#918A7E]">
              Enquiries submitted from the sponsorship page.
            </p>
          </button>
        </aside>

        {showLeads && (
          <section
            data-testid="sponsor-leads-panel"
            className="min-h-[70vh] rounded-3xl border border-[#D4AF37]/20 bg-[#0E0C0A] p-6"
          >
            <div className="flex items-center justify-between border-b border-[#D4AF37]/15 pb-4">
              <h2 className="font-display text-xl text-[#F3E5AB]">Sponsor Leads</h2>
              <button
                type="button"
                data-testid="leads-back-btn"
                onClick={() => setShowLeads(false)}
                className="rounded-full border border-[#D4AF37]/30 px-4 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] hover:bg-[#D4AF37]/10"
              >
                Back to Agents
              </button>
            </div>
            <div className="mt-6 space-y-4">
              {!leads && <p className="text-sm text-[#918A7E]">Loading…</p>}
              {leads && leads.length === 0 && (
                <p className="text-sm text-[#918A7E]">No sponsorship enquiries yet.</p>
              )}
              {leads &&
                leads.map((l, i) => (
                  <div
                    key={i}
                    data-testid={`lead-card-${i + 1}`}
                    className="rounded-2xl border border-[#D4AF37]/20 bg-[#161310] p-5"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="font-display text-lg text-[#F7F4EF]">{l.name}</p>
                      <p className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-[#F3E5AB]">
                        {l.package}
                      </p>
                    </div>
                    <p className="mt-1 text-sm text-[#C8C2B7]">
                      {l.company ? `${l.company} · ` : ""}
                      {l.phone} · {l.email}
                    </p>
                    {l.message && (
                      <p className="mt-2 text-sm leading-relaxed text-[#918A7E]">{l.message}</p>
                    )}
                    <p className="mt-2 text-xs text-[#918A7E]">
                      {new Date(l.ts).toLocaleString("en-IN")}
                    </p>
                  </div>
                ))}
            </div>
          </section>
        )}

        <section className={`${showLeads ? "hidden" : "flex"} min-h-[70vh] flex-col rounded-3xl border border-[#D4AF37]/20 bg-[#0E0C0A]`}>
          <div className="border-b border-[#D4AF37]/15 px-6 py-4">
            <h2 className="font-display text-xl text-[#F3E5AB]">
              {activeAgent ? activeAgent.name : "Agent"}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-[#918A7E] sm:text-sm">
              {activeAgent ? activeAgent.description : ""}
            </p>
          </div>

          <div data-testid="chat-thread" className="flex-1 space-y-5 overflow-y-auto px-6 py-6" style={{ maxHeight: "58vh" }}>
            {thread.length === 0 && (
              <div className="pt-6 text-center">
                <p className="font-accent text-lg italic text-[#C8C2B7]">
                  Ask the {activeAgent ? activeAgent.name : "agent"} anything — it answers with the
                  verified event data built in.
                </p>
              </div>
            )}
            {thread.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-5 py-4 text-sm leading-[1.75] sm:text-base ${
                    m.role === "user"
                      ? "bg-[#D4AF37]/15 text-[#F3E5AB]"
                      : "border border-[#D4AF37]/15 bg-[#161310] text-[#F7F4EF]"
                  }`}
                >
                  {m.content || (busy && m.role === "assistant" ? "…" : "")}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-[#D4AF37]/15 px-6 py-4">
            <div className="mb-3 flex flex-wrap gap-2">
              {(QUICK_PROMPTS[active] || []).map((qp, i) => (
                <button
                  key={qp}
                  type="button"
                  data-testid={`quick-prompt-${i}`}
                  onClick={() => send(qp)}
                  disabled={busy}
                  className="rounded-full border border-[#D4AF37]/25 px-4 py-2 text-xs text-[#C8C2B7] transition-colors hover:border-[#D4AF37]/50 hover:text-[#F3E5AB] disabled:opacity-40"
                >
                  {qp}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              <input
                type="text"
                data-testid="console-message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={`Message the ${activeAgent ? activeAgent.name : "agent"}…`}
                className="min-h-[52px] flex-1 rounded-xl border border-[#D4AF37]/25 bg-[#080705] px-4 text-base text-[#F7F4EF] placeholder:text-[#918A7E] focus:border-[#D4AF37] focus:outline-none"
              />
              <button
                type="button"
                data-testid="console-send-button"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                className="btn-gold min-h-[52px] rounded-full px-8 text-sm font-bold uppercase tracking-[0.12em] text-[#0A0805] disabled:opacity-50"
              >
                {busy ? "…" : "Send"}
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
